import { createFileRoute } from "@tanstack/react-router";
import {
	BadgeCheck,
	CalendarDays,
	GitBranch,
	MessageSquareText,
	ShieldCheck,
	Sparkles,
} from "lucide-react";
import { FeatureGrid } from "@/components/feature-grid";
import { MarketingHero } from "@/components/marketing-hero";
import { MarketingPageShell } from "@/components/marketing-page-shell";
import { MarketingSection } from "@/components/marketing-section";
import { HomepageAgentInstall } from "@/components/setup/homepage-agent-install";
import { buildPageHead } from "@/lib/seo-meta";

const path = "/ai-social-media-agent";

export const Route = createFileRoute(path)({
	component: AiSocialMediaAgentPage,
	head: ({ matches }) =>
		buildPageHead({
			matches,
			internalPath: path,
			title: "AgentReacher | AI Social Media Agent for Teams",
			description:
				"Turn ChatGPT, Claude, Cursor, and other AI agents into social media marketers. Draft, adapt, approve, schedule, and publish posts across every channel with AgentReacher.",
		}),
});

function AiSocialMediaAgentPage() {
	return (
		<MarketingPageShell>
			<MarketingHero
				eyebrow="AI agent social media operator"
				title="Your AI agent should not stop at writing posts."
				description="AgentReacher gives ChatGPT, Claude, Cursor, Codex, and other agents the missing social media layer: create the campaign, adapt it per network, get approval, schedule it, and publish."
				primaryCta={{ label: "Start publishing with an agent", to: "/signup" }}
				secondaryCta={{ label: "See MCP setup", to: "/mcp-server" }}
			/>

			<MarketingSection
				title="From prompt to published, in one workflow"
				description="Classic schedulers own the calendar. AgentReacher owns the moment before the calendar, when an AI assistant already has the context, the campaign, the voice, and the launch plan."
			>
				<FeatureGrid
					features={[
						{
							icon: MessageSquareText,
							title: "Prompt",
							description:
								"Drop in a launch note, changelog, blog post, idea list, or campaign brief.",
						},
						{
							icon: Sparkles,
							title: "Adapt",
							description:
								"AgentReacher turns one source into platform-native posts with the right hook, length, and format.",
						},
						{
							icon: ShieldCheck,
							title: "Approve",
							description:
								"Review drafts with your team before anything reaches a live social account.",
						},
						{
							icon: CalendarDays,
							title: "Publish",
							description:
								"Queue the calendar and let the scheduler ship posts at the right time.",
						},
					]}
				/>
			</MarketingSection>

			<MarketingSection
				title="Not another AI writer. The publishing operator behind the agent."
				description="More posts shipped, fewer manual handoffs, and a reliable path from idea to approved queue."
			>
				<FeatureGrid
					features={[
						{
							icon: BadgeCheck,
							title: "Founder-led marketing",
							description:
								"Turn product decisions, customer learnings, and shipped work into daily LinkedIn and X posts without a weekly blank-page session.",
						},
						{
							icon: BadgeCheck,
							title: "B2B SaaS launches",
							description:
								"Convert one release into founder posts, company updates, threads, launch-day reminders, and follow-ups.",
						},
						{
							icon: BadgeCheck,
							title: "Agency production",
							description:
								"Give every client a workspace, brand voice, approval flow, media library, and connected accounts.",
						},
					]}
				/>
			</MarketingSection>

			<MarketingSection
				title="Why teams are switching now"
				description="Social scheduling is becoming agent infrastructure. The workflow already starts in chat, code editors, docs, and launch plans."
			>
				<FeatureGrid
					columns={2}
					features={[
						{
							icon: GitBranch,
							title: "Connect every agent through MCP",
							description:
								"Plug into ChatGPT, Claude, Cursor, Codex, and the OpenAI Agents SDK so agents can create and schedule posts from the tools teams already use.",
						},
						{
							icon: ShieldCheck,
							title: "Approvals stay in one place",
							description:
								"Keep approvals, roles, workspaces, and calendars in one place instead of scattering drafts across chat history.",
						},
						{
							icon: Sparkles,
							title: "One source, every channel",
							description:
								"Repurpose one source into channel-specific posts that fit each network instead of copy-pasting the same caption everywhere.",
						},
						{
							icon: CalendarDays,
							title: "Hosted, not self-hosted",
							description:
								"Give agencies and teams a hosted workflow without asking them to maintain self-hosted social tooling.",
						},
					]}
				/>
			</MarketingSection>

			<HomepageAgentInstall />
		</MarketingPageShell>
	);
}
